/**
 * @fileoverview Command Component
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @based-on shadcn/ui + Figma design specification
 */

import { forwardRef, createContext, useContext, useState, ReactNode } from 'react';
import { cn } from '../lib/utils';
import { Search } from 'lucide-react';

export interface CommandProps {
  children: ReactNode;
  className?: string;
}

export interface CommandInputProps {
  placeholder?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

export interface CommandListProps {
  children: ReactNode;
  className?: string;
}

export interface CommandEmptyProps {
  children: ReactNode;
  className?: string;
}

export interface CommandGroupProps {
  heading?: string;
  children: ReactNode;
  className?: string;
}

export interface CommandItemProps {
  value?: string;
  onSelect?: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

export interface CommandSeparatorProps {
  className?: string;
}

export interface CommandShortcutProps {
  children: ReactNode;
  className?: string;
}

interface CommandContextValue {
  search: string;
  setSearch: (value: string) => void;
}

const CommandContext = createContext<CommandContextValue | null>(null);

export const Command = forwardRef<HTMLDivElement, CommandProps>(
  ({ children, className }, ref) => {
    const [search, setSearch] = useState('');

    return (
      <CommandContext.Provider value={{ search, setSearch }}>
        <div
          ref={ref}
          className={cn(
            'flex h-full w-full flex-col overflow-hidden rounded-lg bg-card',
            className
          )}
        >
          {children}
        </div>
      </CommandContext.Provider>
    );
  }
);
Command.displayName = 'Command';

export const CommandInput = forwardRef<HTMLInputElement, CommandInputProps>(
  ({ placeholder = 'Search...', value, onValueChange, className }, ref) => {
    const context = useContext(CommandContext);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      context?.setSearch(newValue);
      onValueChange?.(newValue);
    };

    return (
      <div className="flex items-center border-b border-accent-2 px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          ref={ref}
          type="text"
          placeholder={placeholder}
          value={value ?? context?.search ?? ''}
          onChange={handleChange}
          className={cn(
            'flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none',
            'placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        />
      </div>
    );
  }
);
CommandInput.displayName = 'CommandInput';

export const CommandList = forwardRef<HTMLDivElement, CommandListProps>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
      >
        {children}
      </div>
    );
  }
);
CommandList.displayName = 'CommandList';

export const CommandEmpty = forwardRef<HTMLDivElement, CommandEmptyProps>(
  ({ children, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('py-6 text-center text-sm text-muted-foreground', className)}
      >
        {children}
      </div>
    );
  }
);
CommandEmpty.displayName = 'CommandEmpty';

export const CommandGroup = forwardRef<HTMLDivElement, CommandGroupProps>(
  ({ heading, children, className }, ref) => {
    return (
      <div ref={ref} className={cn('overflow-hidden p-1', className)}>
        {heading && (
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {heading}
          </div>
        )}
        {children}
      </div>
    );
  }
);
CommandGroup.displayName = 'CommandGroup';

export const CommandItem = forwardRef<HTMLDivElement, CommandItemProps>(
  ({ value, onSelect, disabled, children, className }, ref) => {
    return (
      <div
        ref={ref}
        data-value={value}
        onClick={() => !disabled && onSelect?.()}
        className={cn(
          'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
          'hover:bg-accent-1 hover:text-foreground',
          disabled && 'pointer-events-none opacity-50',
          className
        )}
      >
        {children}
      </div>
    );
  }
);
CommandItem.displayName = 'CommandItem';

export const CommandSeparator = forwardRef<HTMLDivElement, CommandSeparatorProps>(
  ({ className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('-mx-1 h-px bg-accent-2', className)}
      />
    );
  }
);
CommandSeparator.displayName = 'CommandSeparator';

export const CommandShortcut = ({ children, className }: CommandShortcutProps) => {
  return (
    <span className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}>
      {children}
    </span>
  );
};
